/**
 * ErrorBoundary.tsx
 *
 * Purpose: Catch render-time exceptions from the route tree and show a
 *          recoverable fallback instead of leaving a blank/white page.
 * Created: 2026-09-21 (PRD001A 001A.7a)
 *
 * WHO: `App.tsx`, wrapping `<Routes>`.
 * WHAT: A class component (React error boundaries must be classes — there is
 *      no hook equivalent) implementing `getDerivedStateFromError`/
 *      `componentDidCatch`. Renders `props.fallback` (or a default panel) when
 *      a descendant throws during render.
 * WHERE: Mythbound.App/src/components/common/ErrorBoundary.tsx.
 * WHEN: Any uncaught render exception in a wrapped subtree — for example (found
 *      in the 001A.1 frontend inventory) a routed page calling `useCharacter()`/
 *      `useSession()` with no provider mounted above it. This wave does not fix
 *      that missing-provider gap (Wave 4 owns Character/Session wiring); this
 *      boundary only stops such a crash from producing a blank page in the
 *      meantime, and is not a substitute for fixing the underlying cause.
 * WHY: `App.tsx` previously rendered `<Router><Routes>...` with nothing
 *      catching a render exception — confirmed in the 001A.1 inventory as an
 *      unhandled crash, not a caught "unavailable" state. PRD001A 001A.7a
 *      requires honest unavailable/error UI instead of a silent blank screen.
 *
 * Also exports `RouteResetErrorBoundary` (added in the 001A.7a parent-review
 * correction round): a thin functional wrapper that re-keys the class
 * boundary by `location.pathname`. React remounts a component whose `key`
 * changed, which gives the boundary a fresh `state.error = null` — so
 * navigating away from a page that crashed recovers the app without a full
 * `window.location.reload()`. The reload button remains for the case where
 * the crash isn't route-specific (e.g. it recurs immediately on the new
 * route too). Must be rendered inside the `Router`, since `useLocation`
 * requires router context — see its use in `App.tsx`.
 */

import React from 'react';
import { Box, Button, Heading, Text, VStack, Center } from '@chakra-ui/react';
import { useLocation } from 'react-router';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback; defaults to a generic "something went wrong" panel. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    // A full reload is the safest recovery from an unknown render-time crash —
    // it re-runs every provider/effect from a clean slate rather than trying
    // to guess which piece of local state caused the throw.
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <Center minH="60vh" p={6}>
          <VStack spacing={4} maxW="sm" textAlign="center">
            <Heading size="md">Something went wrong</Heading>
            <Text color="gray.500">
              This page hit an unexpected error. Reloading usually fixes it.
            </Text>
            <Box pt={2}>
              <Button colorScheme="blue" onClick={this.handleReload}>
                Reload
              </Button>
            </Box>
          </VStack>
        </Center>
      );
    }
    return this.props.children;
  }
}

/**
 * Route-aware wrapper: renders `ErrorBoundary` keyed by the current route's
 * pathname, so a crash on one page doesn't persist after the user navigates
 * away from it. Must be rendered inside `<Router>`/`<BrowserRouter>`.
 */
export const RouteResetErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback }) => {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
